import type { AxiosError } from 'axios';
import {
  OryNetworkError,
  OryBrowserRedirectError,
  OryFlowValidationError,
  OryServiceError,
  type OryErrorResponse,
  type OryKratosFlowWithUI,
  isAxiosErrorCode,
} from '../model';
import { OryMessageReader, OryFlowReader, type OrySemanticErrorCode, isOryFlowWithUI } from '../utils';
import { isErrorBrowserRedirect, isErrorEnvelope, isFlowResponse, isErrorContinueWith } from './response-guards';
import { extractRedirectUrl, extractContinueWithFlowUrl } from './redirect-handler';

/**
 * Transforms Axios errors into domain-specific error types.
 * 
 * Error transformation hierarchy:
 * 1. Network errors (no response/5xx/CORS-blocked 429) → OryNetworkError
 * 2. Browser redirect error (410/422 with redirect_browser_to) → OryBrowserRedirectError
 * 2b. Continue-with error (show_verification_ui, etc.) → OryBrowserRedirectError
 * 3. Validation errors (400/422 with UI) → OryFlowValidationError
 * 4. Service errors (401/403/404) → OryServiceError
 * 5. Server errors (5xx fallback) → OryNetworkError
 * 
 * Extracts Ory metadata (requestId, reason, debug) for logging. Falls back to 'http_{status}'
 * when error.id is unavailable (e.g., session 401 responses).
 */
export const transformOryError = (error: AxiosError<OryErrorResponse>): Error => {
  const responseData = error.response?.data;
  const status = error.response?.status;
  const requestUrl = error.config?.url ?? error.request?.responseURL ?? '';

  // 1. Network error - no response received (CORS, timeout, offline, DNS failure, rate limiting)
  // Note: On Ory Network, rate limiting (429) appears as network error due to CORS blocking
  if (!error.response) {
    // Categorize network errors by Axios error codes
    let message = 'Network error - no response received';
    
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      message = 'Request timeout - please try again';
    } else if (error.code === 'ERR_NETWORK') {
      // ERR_NETWORK can mean:
      // 1. Actual network/internet issue
      // 2. CORS blocking (e.g., 429 on preflight)
      // 3. Rate limiting at proxy/CDN level
      // Since we can't distinguish, provide helpful generic message
      message = 'Request failed - please check your connection or wait a moment before retrying';
    } else if (error.code === 'ERR_CANCELED') {
      message = 'Request was cancelled';
    } else if (error.message) {
      message = error.message;
    }

    // Validate and narrow Axios error code for type safety
    const axiosCode = isAxiosErrorCode(error.code) ? error.code : undefined;

    return new OryNetworkError(
      undefined, // No status code when no response
      message,
      requestUrl,
      error,
      error.code !== 'ERR_CANCELED', // Don't retry cancelled requests
      new Date().toISOString(),
      axiosCode, // Type-safe Axios error code
      undefined // No requestId available for network errors
    );
  }

  // 1b. AAL-required errors (403 with session_aal*_required)
  // These are browser redirect errors - session exists but doesn't meet required AAL.
  // The error includes redirect_browser_to pointing to the AAL upgrade flow.
  // Application layer (AuthService.getSession) handles these contextually:
  // - Bootstrap: catches and returns null (prevents redirect loops)
  // - Flow execution: lets redirect propagate (proper navigation)
  // Must check BEFORE general isErrorBrowserRedirect to preserve AAL-specific error code.
  if (status === 403 && isErrorEnvelope(responseData)) {
    const errorId = responseData.error?.id;
    if (errorId === 'session_aal1_required' || errorId === 'session_aal2_required') {
      // Extract redirect URL from Ory response
      // Check both top-level and nested locations
      const redirectTo = responseData.redirect_browser_to || 
                         responseData.error?.details?.['redirect_browser_to'];
      
      if (redirectTo && typeof redirectTo === 'string') {
        // AAL error with redirect - return proper redirect error
        return new OryBrowserRedirectError(
          redirectTo,
          errorId,  // Preserve AAL-specific code
          status,
          responseData.error?.request ?? responseData.error?.request_id
        );
      }
      
      // AAL error without redirect (shouldn't happen) - fallback to service error
      return new OryServiceError(
        errorId,
        responseData.error?.message ?? 'Session does not meet required AAL',
        status,
        responseData.error?.request ?? responseData.error?.request_id,
        responseData.error?.reason,
        responseData.error?.debug
      );
    }
  }

  // 2. Browser redirect error (410 Gone, 422 browser_location_change_required)
  // Handles: flow expired, flow replaced, OAuth2 consent continuation
  if (isErrorBrowserRedirect(responseData)) {
    const code = responseData.error?.id ?? 'browser_location_change_required';
    const redirectTo = extractRedirectUrl(responseData, requestUrl);

    if (!redirectTo) {
      // Should never happen if isRedirectResponse returned true, but handle gracefully
      return new Error(`Browser redirect required but no URL found (HTTP ${status})`);
    }

    return new OryBrowserRedirectError(
      redirectTo,
      code,
      status,
      responseData.error?.request // Request correlation ID if available
    );
  }

  // 2b. Error with continue_with UI actions (show_verification_ui, show_settings_ui, show_recovery_ui)
  // Handles: login blocked due to unverified email, settings requiring verification, etc.
  // Ory returns these as error envelopes with continue_with array containing flow URLs.
  if (isErrorContinueWith(responseData)) {
    const code = responseData.error?.id ?? 'browser_location_change_required';
    const redirectTo = extractContinueWithFlowUrl(responseData);

    if (!redirectTo) {
      // Should never happen if isErrorContinueWith returned true
      return new Error(`Continue-with flow URL required but not found (HTTP ${status})`);
    }

    return new OryBrowserRedirectError(
      redirectTo,
      code,
      status,
      responseData.error?.request // Request correlation ID if available
    );
  }

  // 3. Flow validation error (400/422 with flow containing UI)
  // Ory returns the flow with ui.messages when validation fails
  // Note: Only flows with UI can have validation errors. Logout flows are token-based.
  if (status && (status === 400 || status === 422) && isFlowResponse(responseData) && isOryFlowWithUI(responseData)) {
    // Try to extract request_id if available (flows typically don't have this)
    // It might be in response headers (X-Request-Id) but Axios doesn't expose it easily
    const requestId = undefined; // Flows don't include request_id in body

    // Derive semantic error code from flow messages
    // Type is now properly narrowed to OryFlowWithUI by isOryFlowWithUI() type guard
    const errorCode = extractSemanticCodeFromFlow(responseData) ?? 'flow_validation_error';

    // Extract human-readable error messages using OryFlowReader helper
    const globalErrors = OryFlowReader.getMessages(responseData, {
      types: ['error'],
      scope: 'global'
    });
    const errorMessage = globalErrors.length > 0
      ? globalErrors.map(m => m.text).join('; ')
      : `Flow validation failed (${status})`;

    return new OryFlowValidationError(
      responseData,
      status,
      errorCode,
      errorMessage,
      requestId
    );
  }

  // 4. Service-level errors (from Ory's error envelope)
  // Common in 401/403/404 responses with error envelope
  if (isErrorEnvelope(responseData)) {
    const oryError = responseData.error;
    const message = oryError.message
      ?? oryError.reason
      ?? oryError.status
      ?? 'Service error';

    // Extract Ory metadata for logging/debugging
    const requestId = oryError.request ?? oryError.request_id;
    const reason = oryError.reason;
    const debug = oryError.debug;

    // Generate error code using semantic hierarchy:
    // 1. Ory's semantic ID (best) - e.g., 'security_csrf_violation'
    // 2. HTTP status text (good) - e.g., 'unauthorized'
    // 3. Numeric fallback (last resort) - e.g., 'http_401'
    // Note: Some endpoints (e.g., session/whoami) don't provide error.id
    const errorCode =
      oryError.id ??
      oryError.status?.replace(/([a-z])([A-Z])/g, '$1_$2').replace(/[\s-]+/g, '_').toLowerCase() ??
      `http_${status ?? 'unknown'}`;

    return new OryServiceError(
      errorCode,
      message,
      status ?? 599,
      requestId,
      reason,
      debug
    );
  }

  // 5. Server error (5xx) or other HTTP errors (fallback)
  const isServerError = !!(status && status >= 500);

  // Validate and narrow Axios error code for type safety
  const axiosCode = isAxiosErrorCode(error.code) ? error.code : undefined;

  return new OryNetworkError(
    status,
    isServerError
      ? `Server error (${status}): ${error.message}`
      : error.message || `HTTP error ${status}`,
    requestUrl,
    error,
    isServerError, // 5xx are retryable
    new Date().toISOString(),
    axiosCode, // Type-safe Axios error code
    undefined // No requestId available for generic HTTP errors
  );
};

/**
 * Validates successful HTTP responses for "hidden" validation errors.
 * 
 * Some Ory flows (e.g., verification) return HTTP 200 with validation errors
 * embedded in the flow's ui.messages instead of returning HTTP 400/422.
 * This function detects such cases and throws OryFlowValidationError.
 * 
 * **Important:** Only validates POST/PUT/PATCH requests (flow submission).
 * GET requests may legitimately return flows with errors from previous submissions.
 * 
 * From Ory docs: "error message can also exist inside the flow data even when 
 * the request is successful. This is the case when retrieving the login flow 
 * using the flow ID and the flow was submitted previously but still contains errors."
 * 
 * @param data - Response data to validate
 * @param status - HTTP status code
 * @param method - HTTP method (GET, POST, etc.)
 * @throws OryFlowValidationError if POST/PUT/PATCH response contains validation errors
 */
export const validateSuccessResponse = (data: unknown, status: number, method: string): void => {
  // Only check successful responses (2xx)
  if (!status || status < 200 || status >= 300) {
    return;
  }

  // Only validate submission operations (POST, PUT, PATCH), not retrieval (GET)
  // GET operations may return flows with errors from previous submissions
  const isSubmitOperation = ['POST', 'PUT', 'PATCH'].includes(method?.toUpperCase());
  if (!isSubmitOperation) {
    return;
  }

  // Check if response is a flow with UI
  if (!isFlowResponse(data) || !isOryFlowWithUI(data)) {
    return;
  }

  // Check if flow contains error messages
  const errorMessages = OryFlowReader.getMessages(data, {
    types: ['error'],
    scope: 'global'
  });

  if (errorMessages.length === 0) {
    return; // No errors - response is genuinely successful
  }

  // Flow contains errors despite HTTP 200 - treat as validation error
  const errorCode = extractSemanticCodeFromFlow(data) ?? 'flow_validation_error';
  const errorMessage = errorMessages.map(m => m.text).join('; ');

  throw new OryFlowValidationError(
    data,
    status,
    errorCode,
    errorMessage,
    undefined // No requestId in successful responses
  );
};

/**
 * Extract semantic error code from Ory flow messages.
 *
 * Uses OryFlowReader helper as single source of truth for flow error extraction.
 * Example: Message ID 4000006 → "INVALID_CREDENTIALS" → "invalid_credentials"
 */
const extractSemanticCodeFromFlow = (flow: OryKratosFlowWithUI): OrySemanticErrorCode | undefined => {
  // ✅ No type assertion needed! Type is properly narrowed by isOryFlowWithUI() type guard
  const globalErrors = OryFlowReader.getMessages(flow, {
    types: ['error'],
    scope: 'global'
  });

  // Get first error message ID
  const firstError = globalErrors[0];

  if (!firstError || typeof firstError.id !== 'number') {
    return undefined;
  }

  // Map Ory message ID to semantic key
  const messageKey = OryMessageReader.getMessageKey(firstError.id);

  if (!messageKey) {
    return undefined;
  }

  // Convert to lowercase with underscores: "INVALID_CREDENTIALS" → "invalid_credentials"
  // Cast is safe: we know messageKey is from OryMessageReader
  return messageKey.toLowerCase() as OrySemanticErrorCode;
};

